/**
 * Rewards Hooks
 *
 * TanStack Query hooks for the rewards claim system.
 * Feature: 017-rewards-claim-system
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getActivePeriod,
  getPublicPeriods,
  submitClaim,
  getMyClaimHistory,
  getPeriods,
  openPeriod,
  closePeriod,
  setRate,
  markDistributed,
  getClaimants,
  exportCSV,
  type ActivePeriodResponse,
  type ClaimPeriod,
  type RewardClaim,
  type ClaimHistoryEntry,
  type OpenPeriodConfig,
  type ClaimantsResponse,
} from '../api/rewards';

// ---- Holder Hooks ----

export function useActivePeriod(projectSlug: string | undefined) {
  return useQuery<ActivePeriodResponse>({
    queryKey: ['rewards', 'active-period', projectSlug],
    queryFn: () => getActivePeriod(projectSlug!),
    enabled: !!projectSlug,
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useSubmitClaim(projectSlug: string) {
  const queryClient = useQueryClient();

  return useMutation<{ claims: RewardClaim[]; claimedCount: number }, Error>({
    mutationFn: () => submitClaim(projectSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rewards', 'active-period', projectSlug] });
    },
  });
}

export function useMyClaimHistory() {
  return useQuery<{ claims: ClaimHistoryEntry[] }>({
    queryKey: ['rewards', 'my-claims'],
    queryFn: () => getMyClaimHistory(),
    staleTime: 60 * 1000,
  });
}

export function usePublicPeriods(projectSlug: string | undefined) {
  return useQuery<{ periods: ClaimPeriod[] }>({
    queryKey: ['rewards', 'public-periods', projectSlug],
    queryFn: () => getPublicPeriods(projectSlug!),
    enabled: !!projectSlug,
    staleTime: 30 * 1000,
  });
}

// ---- Owner Hooks ----

export function usePeriods(projectSlug: string | undefined) {
  return useQuery<{ periods: ClaimPeriod[] }>({
    queryKey: ['rewards', 'periods', projectSlug],
    queryFn: () => getPeriods(projectSlug!),
    enabled: !!projectSlug,
    staleTime: 30 * 1000,
  });
}

export function useOpenPeriod(projectSlug: string) {
  const queryClient = useQueryClient();

  return useMutation<{ period: ClaimPeriod }, Error, OpenPeriodConfig>({
    mutationFn: (config) => openPeriod(projectSlug, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rewards', 'active-period', projectSlug] });
      queryClient.invalidateQueries({ queryKey: ['rewards', 'periods', projectSlug] });
    },
  });
}

export function useClosePeriod(projectSlug: string) {
  const queryClient = useQueryClient();

  return useMutation<{ period: ClaimPeriod }, Error, string>({
    mutationFn: (periodId) => closePeriod(projectSlug, periodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rewards', 'active-period', projectSlug] });
      queryClient.invalidateQueries({ queryKey: ['rewards', 'periods', projectSlug] });
      queryClient.invalidateQueries({ queryKey: ['rewards', 'claimants', projectSlug] });
      queryClient.invalidateQueries({ queryKey: ['rewards', 'public-periods', projectSlug] });
    },
  });
}

export function useSetRate(projectSlug: string) {
  const queryClient = useQueryClient();

  return useMutation<
    { period: ClaimPeriod },
    Error,
    { periodId: string; rate_mode: string; rate_per_point?: number; total_pool?: number }
  >({
    mutationFn: ({ periodId, ...data }) => setRate(projectSlug, periodId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rewards', 'periods', projectSlug] });
      queryClient.invalidateQueries({ queryKey: ['rewards', 'claimants', projectSlug] });
    },
  });
}

export function useMarkDistributed(projectSlug: string) {
  const queryClient = useQueryClient();

  return useMutation<{ period: ClaimPeriod }, Error, string>({
    mutationFn: (periodId) => markDistributed(projectSlug, periodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rewards', 'periods', projectSlug] });
    },
  });
}

export function useClaimants(projectSlug: string | undefined, periodId: string | undefined) {
  return useQuery<ClaimantsResponse>({
    queryKey: ['rewards', 'claimants', projectSlug, periodId],
    queryFn: () => getClaimants(projectSlug!, periodId!),
    enabled: !!projectSlug && !!periodId,
    staleTime: 30 * 1000,
  });
}

export function useExportCSV(projectSlug: string) {
  return useMutation<void, Error, string>({
    mutationFn: (periodId) => exportCSV(projectSlug, periodId),
  });
}
